import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, parse } from "date-fns";

const getAvailabilityColor = (estimated, installed, pending) => {
  const totalUsed = (installed || 0) + (pending || 0);
  const available = (estimated || 0) - totalUsed;
  const percentage = estimated > 0 ? (available / estimated) * 100 : 0;

  if (percentage >= 60) {
    return {
      color: "bg-green-400",
      textColor: "text-green-400",
      percentage,
      available,
    };
  } else if (percentage >= 20) {
    return {
      color: "bg-yellow-400",
      textColor: "text-yellow-400",
      percentage,
      available,
    };
  } else {
    return {
      color: "bg-red-400",
      textColor: "text-red-400",
      percentage,
      available,
    };
  }
};

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-orange-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-orange-400 hover:bg-orange-500",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-orange-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const formatCurrency = (num) =>
  `$${Number(num || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PieceworkerLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [log, setLog] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [groupByTitle, setGroupByTitle] = useState(false);

  // ── Additional items (add from work order during edit) ──
  const [additionalItems, setAdditionalItems] = useState([]);
  const [addItemQuantities, setAddItemQuantities] = useState({});
  const [showAddItems, setShowAddItems] = useState(false);
  const [addItemsLoading, setAddItemsLoading] = useState(false);
  const [addItemBidAreaFilter, setAddItemBidAreaFilter] = useState("");
  const [addItemBidAreas, setAddItemBidAreas] = useState([]);

  const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // ════════════════════════════════════════════════════════════════════════
  // fetchLog — fetches from net-assemblies endpoint (same source
  // WorkOrderViewNew uses) so budget quantities are consistent.
  // ════════════════════════════════════════════════════════════════════════
  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE}/pieceworkerLogs2/${id}`, {
        headers,
      });

      const logData = response.data.data;

      // ── Fetch net-assemblies (same source WorkOrderViewNew uses) ──
      const projectId = logData.project?._id || logData.project;
      const workOrderId = logData.workOrder?._id || logData.workOrder;

      if (projectId && workOrderId) {
        try {
          const netRes = await axios.get(
            `${API_BASE}/net-assemblies/project/${projectId}`,
            { headers, params: { category: "L" } },
          );

          const allNetLabor = netRes.data?.data || [];

          const woNetItems = allNetLabor.filter((item) => {
            const itemWO = item.linkedWO?._id
              ? item.linkedWO._id.toString()
              : (item.linkedWO || "").toString();
            return itemWO === workOrderId.toString();
          });

          const netItemsMap = new Map();
          woNetItems.forEach((item) => {
            const keyId = (
              item.lineItemId ||
              item.key ||
              item._id ||
              ""
            ).toString();
            if (keyId) netItemsMap.set(keyId, item);

            const origId = (item.originalLineItemId || "").toString();
            if (origId && !netItemsMap.has(origId)) {
              netItemsMap.set(origId, item);
            }
          });

          if (logData.lineItems && netItemsMap.size > 0) {
            logData.lineItems = logData.lineItems.map((item) => {
              const lineItemId = (item.lineItemId || "").toString();
              const netItem =
                netItemsMap.get(lineItemId) ||
                netItemsMap.get((item.originalLineItemId || "").toString());

              if (netItem) {
                return {
                  ...item,
                  currentEstimated: netItem.netRequiredQty || 0,
                  currentApproved: netItem.qtyOrdered || 0,
                  currentPending: netItem.qtyPending || 0,
                  currentAvailable: netItem.netRemaining || 0,
                  budgetAmount: netItem.amount || 0,
                  budgetUnitCost:
                    netItem.price || netItem.unitRate || item.unitCost || 0,
                  conditionName:
                    netItem.conditionName || item.conditionName || "",
                  sectionNumber:
                    netItem.sectionNumber || item.sectionNumber || "",
                  isFromVariance:
                    item.isFromVariance ||
                    netItem.hasVariance ||
                    netItem.isNewSpec ||
                    false,
                  closedOutBySizeChange: netItem.closedOutBySizeChange || false,
                  isNewSpec: netItem.isNewSpec || false,
                };
              }
              return item;
            });
          }
        } catch (netErr) {
          console.warn(
            "Failed to fetch net-assemblies, using backend enrichment:",
            netErr.message,
          );
        }
      }

      setLog(logData);
      setSelectedEmployeeId(logData.user?._id || logData.user);

      const initialQuantities = {};
      logData.lineItems?.forEach((item) => {
        initialQuantities[item.lineItemId] = item.qtyInstalled || 0;
      });
      setQuantities(initialQuantities);

      const uniqueBidAreas = [
        ...new Set(
          logData.lineItems?.map((item) => item.bidArea).filter(Boolean),
        ),
      ];
      setBidAreas(uniqueBidAreas);

      if (logData.status === "pending") {
        const employeesResponse = await axios.get(
          `${API_BASE}/users/pieceworkers`,
          { headers },
        );
        setEmployees(employeesResponse.data);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load pieceworker log.",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    if (!loading) window.scrollTo(0, 0);
  }, [loading]);

  // ════════════════════════════════════════════════════════════════════════
  // Enter edit mode — also fetches available WO items for adding new ones
  // ════════════════════════════════════════════════════════════════════════
  const enterEditMode = async () => {
    setEditMode(true);
    setShowAddItems(false);
    setAdditionalItems([]);
    setAddItemQuantities({});

    const workOrderId = log?.workOrder?._id || log?.workOrder;
    if (!workOrderId) return;

    try {
      setAddItemsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE}/pieceworkerLogs2/work-order/${workOrderId}/available-items`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const allAvailable = response.data.items || [];

      // Filter out items already on the log
      const existingIds = new Set(
        (log.lineItems || []).map((li) => (li.lineItemId || "").toString()),
      );
      const newItems = allAvailable.filter(
        (item) => !existingIds.has((item._id || "").toString()),
      );

      setAdditionalItems(newItems);

      const initQtys = {};
      newItems.forEach((item) => {
        initQtys[item._id] = "";
      });
      setAddItemQuantities(initQtys);

      const areas = [
        ...new Set(newItems.map((item) => item.bidArea).filter(Boolean)),
      ];
      setAddItemBidAreas(areas);
      setAddItemBidAreaFilter("");
    } catch (err) {
      console.warn("Failed to fetch available items:", err.message);
    } finally {
      setAddItemsLoading(false);
    }
  };

  const exitEditMode = () => {
    setEditMode(false);
    setShowAddItems(false);
    setAdditionalItems([]);
    setAddItemQuantities({});
    const resetQuantities = {};
    log?.lineItems?.forEach((item) => {
      resetQuantities[item.lineItemId] = item.qtyInstalled || 0;
    });
    setQuantities(resetQuantities);
  };

  const handleQuantityChange = (lineItemId, value) => {
    const numValue = value === "" ? 0 : parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;

    const item = log?.lineItems?.find((li) => li.lineItemId === lineItemId);

    if (item && numValue > 0) {
      const maxAvailable =
        (item.currentAvailable || 0) + (item.qtyInstalled || 0);
      if (numValue > maxAvailable) {
        setDialog({
          open: true,
          type: "error",
          title: "Invalid Quantity",
          message: `Quantity cannot exceed ${maxAvailable} ${item.uom}.`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }

    setQuantities((prev) => ({ ...prev, [lineItemId]: numValue }));
  };

  const handleAddItemQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = additionalItems.find((li) => li._id === itemId);

    if (item && numValue > item.qtyRemaining) {
      setDialog({
        open: true,
        type: "error",
        title: "Invalid Quantity",
        message: `Quantity cannot exceed ${item.qtyRemaining} ${item.uom}.`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      setAddItemQuantities((prev) => ({
        ...prev,
        [itemId]: item.qtyRemaining.toString(),
      }));
      return;
    }

    setAddItemQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleEmployeeChange = async (employeeId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_BASE}/pieceworkerLogs2/${id}`,
        { employeeId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSelectedEmployeeId(employeeId);
      setLog((prev) => ({
        ...prev,
        user: employees.find((emp) => emp._id === employeeId),
      }));

      setDialog({
        open: true,
        type: "success",
        title: "Employee Updated",
        message: "The pieceworker has been successfully updated.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message: err.response?.data?.message || "Failed to update pieceworker.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Save — includes both existing line items AND newly added items
  // ════════════════════════════════════════════════════════════════════════
  const handleSaveChanges = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");

      // Existing line items with updated quantities
      const existingLineItems = log.lineItems
        .map((item) => ({
          lineItemId: item.lineItemId,
          qtyInstalled: parseFloat(quantities[item.lineItemId] || 0),
          title: item.title,
          uom: item.uom,
          unitCost: item.unitCost,
          bidArea: item.bidArea || "",
        }))
        .filter((item) => item.qtyInstalled > 0);

      // Newly added items from the work order
      const newLineItems = additionalItems
        .filter((item) => {
          const qty = parseFloat(addItemQuantities[item._id] || 0);
          return qty > 0;
        })
        .map((item) => ({
          lineItemId: item._id,
          qtyInstalled: parseFloat(addItemQuantities[item._id]),
          title: item.title,
          uom: item.uom,
          unitCost: item.unitCost,
          bidArea: item.bidArea || "",
        }));

      const combinedLineItems = [...existingLineItems, ...newLineItems];

      if (combinedLineItems.length === 0) {
        setDialog({
          open: true,
          type: "error",
          title: "No Valid Line Items",
          message:
            "At least one line item with a quantity greater than 0 is required.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      await axios.patch(
        `${API_BASE}/pieceworkerLogs2/${id}`,
        {
          lineItems: combinedLineItems,
          employeeId: selectedEmployeeId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await fetchLog();
      setEditMode(false);
      setShowAddItems(false);
      setAdditionalItems([]);
      setAddItemQuantities({});

      setDialog({
        open: true,
        type: "success",
        title: "Worksheet Updated",
        message: `The pieceworker worksheet has been successfully updated.${newLineItems.length > 0 ? ` ${newLineItems.length} new item(s) added.` : ""}`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message: err.response?.data?.message || "Failed to update worksheet.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus, reason = "") => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");

      const endpoint =
        newStatus === "approved"
          ? `${API_BASE}/pieceworkerLogs2/${id}/approve`
          : newStatus === "rejected"
            ? `${API_BASE}/pieceworkerLogs2/${id}/reject`
            : `${API_BASE}/pieceworkerLogs2/${id}/reactivate`;

      const payload = newStatus === "rejected" ? { reason } : {};

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const tabFromStatus = {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      }[newStatus];

      const targetStatusTab = location.state?.activeStatusTab || tabFromStatus;
      const projectId = log?.project?._id || location.state?.projectId;

      if (projectId && !location.state?.fromMainTab) {
        navigate(`/Projects/${projectId}`, {
          state: {
            mainTab: location.state?.mainTab || "Operations",
            initialTab: location.state?.activeTab || "Worksheets",
            activeStatusTab: targetStatusTab,
          },
        });
      } else {
        navigate("/pieceworkerlog", {
          state: { activeTab: targetStatusTab, fromLogs: true },
        });
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Action Failed",
        message: err.response?.data?.message || "Failed to update status.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const showConfirmationDialog = (status) => {
    const action =
      status === "approved"
        ? "approve"
        : status === "rejected"
          ? "reject"
          : "reactivate";

    setDialog({
      open: true,
      type: status === "approved" ? "success" : "error",
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      message: `Are you sure you want to ${action} this pieceworker log?`,
      onConfirm: () => {
        setDialog((prev) => ({ ...prev, open: false }));
        handleUpdateStatus(status);
      },
    });
  };

  const handleBack = () => {
    const targetStatusTab =
      location.state?.activeStatusTab ||
      location.state?.activeTab ||
      { pending: "Pending", approved: "Approved", rejected: "Rejected" }[
        log?.status
      ] ||
      "Pending";

    // Write the correct tab back so main tab opens there
    sessionStorage.setItem("pwlog_activeTab", targetStatusTab);

    const projectId = log?.project?._id || location.state?.projectId;

    if (location.state?.fromMainTab) {
      navigate("/pieceworkerlog");
    } else if (projectId && location.state?.fromProject) {
      navigate(`/Projects/${projectId}`, {
        state: {
          mainTab: location.state?.mainTab || "Operations",
          initialTab: location.state?.activeTab || "Worksheets",
          activeStatusTab: targetStatusTab,
        },
      });
    } else {
      navigate("/pieceworkerlog");
    }
  };

  const filteredLineItems = (log?.lineItems || []).filter(
    (item) => !bidAreaFilter || item.bidArea === bidAreaFilter,
  );

  // Filtered additional items for the "Add Items" panel
  const filteredAdditionalItems = additionalItems.filter(
    (item) => !addItemBidAreaFilter || item.bidArea === addItemBidAreaFilter,
  );

  // Count of additional items with qty > 0
  const addedItemCount = additionalItems.filter(
    (item) => parseFloat(addItemQuantities[item._id] || 0) > 0,
  ).length;

  const groupedLineItems = useMemo(() => {
    if (!groupByTitle) return null;

    const map = new Map();
    filteredLineItems.forEach((item) => {
      const key = `${(item.title || "Untitled").trim()}||${item.uom || ""}||${item.bidArea || ""}`;

      if (!map.has(key)) {
        map.set(key, {
          _groupKey: key,
          title: item.title || "Untitled",
          uom: item.uom || "",
          bidArea: item.bidArea || "",
          unitCost: item.unitCost || 0,
          _totalEstimated: 0,
          _totalApproved: 0,
          _totalPending: 0,
          _totalAvailable: 0,
          _totalReported: 0,
          _totalBudgetAmount: 0,
          _totalSubtotal: 0,
          _totalBurden: 0,
          _totalTotal: 0,
          _count: 0,
          _hasVariance: false,
          _hasClosed: false,
          _hasNewSpec: false,
          _assemblies: new Set(),
        });
      }

      const group = map.get(key);
      group._count += 1;
      group._totalEstimated += item.currentEstimated || 0;
      group._totalApproved += item.currentApproved || 0;
      group._totalPending += item.currentPending || 0;
      group._totalAvailable += item.currentAvailable || 0;
      group._totalReported += item.qtyInstalled || 0;
      group._totalBudgetAmount +=
        item.budgetAmount != null
          ? item.budgetAmount
          : (item.currentEstimated || 0) *
            (item.budgetUnitCost || item.unitCost || 0);
      group._totalSubtotal += item.subtotal || 0;
      group._totalBurden += item.burden || 0;
      group._totalTotal += item.total || 0;
      if (item.isFromVariance) group._hasVariance = true;
      if (item.closedOutBySizeChange) group._hasClosed = true;
      if (item.isNewSpec) group._hasNewSpec = true;
      if (item.conditionName) group._assemblies.add(item.conditionName);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.title.localeCompare(b.title, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [filteredLineItems, groupByTitle]);

  const totals = (() => {
    if (!log?.lineItems) return { subtotal: 0, burden: 0, total: 0 };
    let subtotal = 0;
    let burden = 0;
    let total = 0;
    log.lineItems.forEach((item) => {
      if (editMode) {
        const editedQty = quantities[item.lineItemId] || 0;
        const perUnit =
          item.qtyInstalled > 0
            ? Math.round((item.subtotal / item.qtyInstalled) * 100) / 100
            : item.unitCost || 0;
        const perUnitB =
          item.qtyInstalled > 0
            ? Math.round((item.burden / item.qtyInstalled) * 100) / 100
            : 0;
        const perUnitT =
          item.qtyInstalled > 0
            ? Math.round((item.total / item.qtyInstalled) * 100) / 100
            : 0;
        subtotal += perUnit * editedQty;
        burden += perUnitB * editedQty;
        total += perUnitT * editedQty;
      } else {
        const qty = item.qtyInstalled || 0;
        const perUnit =
          qty > 0
            ? Math.round((item.subtotal / qty) * 100) / 100
            : item.unitCost || 0;
        const perUnitB =
          qty > 0 ? Math.round((item.burden / qty) * 100) / 100 : 0;
        const perUnitT =
          qty > 0 ? Math.round((item.total / qty) * 100) / 100 : 0;
        subtotal += perUnit * qty;
        burden += perUnitB * qty;
        total += perUnitT * qty;
      }
    });
    return { subtotal, burden, total };
  })();

  const budgetTotals = (() => {
    if (!log?.lineItems) return { estimatedAmount: 0 };
    let estimatedAmount = 0;
    log.lineItems.forEach((item) => {
      estimatedAmount +=
        item.budgetAmount != null
          ? item.budgetAmount
          : (item.currentEstimated || 0) *
            (item.budgetUnitCost || item.unitCost || 0);
    });
    return { estimatedAmount };
  })();

  // Calculate subtotal for newly added items
  const addItemsSubtotal = additionalItems.reduce((sum, item) => {
    const qty = parseFloat(addItemQuantities[item._id] || 0);
    return sum + qty * (item.unitCost || 0);
  }, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">Log not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-700">
      <div className="mx-auto px-4 py-6">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            <ArrowLeftIcon className="size-4 mr-2" />
            Back to Worksheets
          </button>
        </div>

        <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-orange-400">
                Worksheet Details
              </h1>
              {log.status === "pending" && !editMode && (
                <button
                  onClick={enterEditMode}
                  className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Edit Worksheet
                </button>
              )}
            </div>

            {/* ── Header Info ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Work Order
                </p>
                <p className="text-sm text-gray-200">
                  {log.workOrder?.title || "N/A"}
                </p>
                <p className="text-xs text-gray-400">{log.woNumber}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Status
                </p>
                <p
                  className={`text-sm font-medium ${
                    log.status === "approved"
                      ? "text-green-400"
                      : log.status === "rejected"
                        ? "text-red-400"
                        : "text-yellow-400"
                  }`}
                >
                  {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Pay Period
                </p>
                <p className="text-sm text-gray-200">
                  {log.workCompletedDate
                    ? format(
                        parse(
                          log.workCompletedDate.split("T")[0],
                          "yyyy-MM-dd",
                          new Date(),
                        ),
                        "MMMM d, yyyy",
                      )
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Submitted
                </p>
                <p className="text-sm text-gray-200">
                  {log.submittedAt
                    ? format(parseISO(log.submittedAt), "MMMM d, yyyy, h:mm a")
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Employee
                </p>
                {editMode ? (
                  <select
                    value={selectedEmployeeId || ""}
                    onChange={(e) => handleEmployeeChange(e.target.value)}
                    className="block w-full max-w-md text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="" disabled>
                      Select an employee
                    </option>
                    {employees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.displayName || employee.email}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-200">
                    {log.user?.displayName || log.user?.email || "N/A"}
                  </p>
                )}
              </div>
              {log.status === "rejected" && log.rejectionReason && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-400">{log.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* ── Line Items Header ── */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-orange-400">
                  Line Items
                </h2>
                {!editMode && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-300 whitespace-nowrap">
                      Group by Title
                    </label>
                    <div
                      className={`relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full transition-colors ${
                        groupByTitle ? "bg-orange-500" : "bg-gray-600"
                      }`}
                      onClick={() => setGroupByTitle(!groupByTitle)}
                    >
                      <span
                        className={`absolute left-1 top-1 block h-4 w-4 rounded-full bg-white transition-transform ${
                          groupByTitle ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>
              {bidAreas.length > 0 && (
                <select
                  value={bidAreaFilter}
                  onChange={(e) => setBidAreaFilter(e.target.value)}
                  className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Bid Areas</option>
                  {bidAreas.map((bidArea) => (
                    <option key={bidArea} value={bidArea}>
                      {bidArea}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* ── Line Items Table ── */}
            <div className="overflow-x-auto rounded-lg shadow-xl">
              {!editMode && groupByTitle && groupedLineItems ? (
                /* ── GROUPED VIEW (read-only) ── */
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Bid Area
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Net Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Budget Amt
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Charged
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Pending
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Available
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Reported
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Subtotal
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Burden
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {groupedLineItems.length > 0 ? (
                      groupedLineItems.map((group, index) => {
                        const availPct =
                          group._totalEstimated > 0
                            ? (group._totalAvailable / group._totalEstimated) *
                              100
                            : 0;
                        const availColor =
                          availPct >= 60
                            ? "text-green-400"
                            : availPct >= 20
                              ? "text-yellow-400"
                              : "text-red-400";

                        return (
                          <tr
                            key={group._groupKey}
                            className={`${index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"} hover:bg-gray-500 transition-colors duration-200`}
                          >
                            <td className="px-4 py-2 text-sm text-gray-200">
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {group.title}
                                </span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {group._hasNewSpec && (
                                    <span className="inline-block w-fit px-1.5 py-0.5 text-xs bg-blue-400/20 text-blue-400 rounded">
                                      NEW SPEC
                                    </span>
                                  )}
                                  {group._hasClosed && (
                                    <span className="inline-block w-fit px-1.5 py-0.5 text-xs bg-gray-500/50 text-gray-300 rounded">
                                      CLOSED
                                    </span>
                                  )}
                                </div>
                                {group._assemblies.size > 0 && (
                                  <span className="text-xs text-gray-500 mt-0.5">
                                    {[...group._assemblies]
                                      .slice(0, 3)
                                      .join(", ")}
                                    {group._assemblies.size > 3 &&
                                      ` +${group._assemblies.size - 3} more`}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {group.bidArea || "N/A"}
                            </td>
                            <td className="px-4 py-2 text-sm text-center">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-500/50 text-gray-300 rounded-full">
                                {group._count}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {group._totalEstimated.toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {group.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-orange-400 text-right font-medium">
                              {formatCurrency(group._totalBudgetAmount)}
                            </td>
                            <td className="px-4 py-2 text-sm text-green-400 text-right">
                              {group._totalApproved.toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {group.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                              {group._totalPending.toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {group.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className={availColor}>
                                {group._totalAvailable.toLocaleString()}{" "}
                                {group.uom}
                              </span>
                              <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                <div
                                  className={`${availColor.replace("text-", "bg-")} h-1.5 rounded-full`}
                                  style={{
                                    width: `${Math.min(Math.max(availPct, 0), 100)}%`,
                                  }}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {group._totalReported.toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {group.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatCurrency(group._totalSubtotal)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatCurrency(group._totalBurden)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right font-medium">
                              {formatCurrency(group._totalTotal)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="12"
                          className="px-4 py-4 text-center text-sm text-gray-400"
                        >
                          No line items found
                          {bidAreaFilter && ` for bid area "${bidAreaFilter}"`}.
                        </td>
                      </tr>
                    )}
                    {groupedLineItems.length > 0 && (
                      <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500">
                        <td
                          className="px-4 py-3 text-sm text-orange-300 text-right"
                          colSpan="2"
                        >
                          TOTALS ({groupedLineItems.length} groups,{" "}
                          {groupedLineItems.reduce((s, g) => s + g._count, 0)}{" "}
                          items)
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {groupedLineItems
                            .reduce((sum, g) => sum + g._totalEstimated, 0)
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400 text-right font-bold">
                          {formatCurrency(
                            groupedLineItems.reduce(
                              (sum, g) => sum + g._totalBudgetAmount,
                              0,
                            ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-400 text-right">
                          {groupedLineItems
                            .reduce((sum, g) => sum + g._totalApproved, 0)
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-yellow-400 text-right">
                          {groupedLineItems
                            .reduce((sum, g) => sum + g._totalPending, 0)
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {groupedLineItems
                            .reduce((sum, g) => sum + g._totalAvailable, 0)
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {groupedLineItems
                            .reduce((sum, g) => sum + g._totalReported, 0)
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatCurrency(
                            groupedLineItems.reduce(
                              (sum, g) => sum + g._totalSubtotal,
                              0,
                            ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatCurrency(
                            groupedLineItems.reduce(
                              (sum, g) => sum + g._totalBurden,
                              0,
                            ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400 text-right font-bold">
                          {formatCurrency(
                            groupedLineItems.reduce(
                              (sum, g) => sum + g._totalTotal,
                              0,
                            ),
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                /* ── FLAT VIEW (edit or read-only) ── */
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Assembly
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Bid Area
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Net Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Budget Amt
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Charged
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Pending
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Available
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Reported
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        <div className="flex flex-col items-end">
                          <span>Unit Cost</span>
                          <span className="text-[0.625rem] text-gray-600 font-normal opacity-80 mt-0.5">
                            (excl. burden)
                          </span>
                        </div>
                      </th>

                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        <div className="flex flex-col items-end">
                          <span>Subtotal</span>
                          <span className="text-[0.625rem] text-gray-600 font-normal opacity-80 mt-0.5">
                            (excl. burden)
                          </span>
                        </div>
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Burden
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {filteredLineItems.length > 0 ? (
                      filteredLineItems.map((item, index) => {
                        const { color, textColor, percentage, available } =
                          getAvailabilityColor(
                            item.currentEstimated,
                            item.currentApproved,
                            item.currentPending,
                          );
                        const budgetAmount =
                          item.budgetAmount != null
                            ? item.budgetAmount
                            : (item.currentEstimated || 0) *
                              (item.budgetUnitCost || item.unitCost || 0);
                        const isClosed = item.closedOutBySizeChange === true;

                        // ← ADD IT RIGHT HERE, before the return
                        const perUnitSubtotal =
                          item.qtyInstalled > 0
                            ? Math.round(
                                (item.subtotal / item.qtyInstalled) * 100,
                              ) / 100
                            : item.unitCost || 0;
                        const perUnitBurden =
                          item.qtyInstalled > 0
                            ? Math.round(
                                (item.burden / item.qtyInstalled) * 100,
                              ) / 100
                            : 0;
                        const perUnitTotal =
                          item.qtyInstalled > 0
                            ? Math.round(
                                (item.total / item.qtyInstalled) * 100,
                              ) / 100
                            : 0;
                        const displaySubtotal =
                          perUnitSubtotal * (item.qtyInstalled || 0);
                        const displayBurden =
                          perUnitBurden * (item.qtyInstalled || 0);
                        const displayTotal =
                          perUnitTotal * (item.qtyInstalled || 0);

                        return (
                          <tr
                            key={item.lineItemId}
                            className={`${index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"} hover:bg-gray-500 transition-colors duration-200 ${isClosed ? "opacity-60" : ""}`}
                          >
                            <td className="px-4 py-2 text-sm text-gray-200">
                              <span className={isClosed ? "line-through" : ""}>
                                {item.title}
                              </span>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {item.isNewSpec && (
                                  <span className="px-1.5 py-0.5 text-xs bg-blue-400/20 text-blue-400 rounded">
                                    NEW SPEC
                                  </span>
                                )}
                                {isClosed && (
                                  <span className="px-1.5 py-0.5 text-xs bg-gray-500/50 text-gray-300 rounded">
                                    CLOSED
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 min-w-64">
                              {item.conditionName || "N/A"}
                              {item.sectionNumber &&
                                item.sectionNumber !== "-" && (
                                  <span className="text-xs text-gray-400 ml-1">
                                    ({item.sectionNumber})
                                  </span>
                                )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {item.bidArea || "N/A"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {isClosed ? (
                                <span className="text-gray-500 line-through">
                                  {(
                                    item.currentEstimated || 0
                                  ).toLocaleString()}
                                </span>
                              ) : (
                                <>
                                  {(
                                    item.currentEstimated || 0
                                  ).toLocaleString()}{" "}
                                  <span className="text-gray-400 text-xs">
                                    {item.uom}
                                  </span>
                                </>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-orange-400 text-right font-medium">
                              {formatCurrency(budgetAmount)}
                            </td>
                            <td className="px-4 py-2 text-sm text-green-400 text-right">
                              {(item.currentApproved || 0).toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {item.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                              {(item.currentPending || 0).toLocaleString()}{" "}
                              <span className="text-gray-400 text-xs">
                                {item.uom}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {isClosed ? (
                                <span className="text-gray-500">Closed</span>
                              ) : (
                                <>
                                  <span className={textColor}>
                                    {available.toLocaleString()} {item.uom}
                                  </span>
                                  <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                    <div
                                      className={`${color.replace("text-", "bg-")} h-1.5 rounded-full`}
                                      style={{
                                        width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {editMode && log.status === "pending" ? (
                                <input
                                  type="number"
                                  value={quantities[item.lineItemId] || ""}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.lineItemId,
                                      e.target.value,
                                    )
                                  }
                                  min="0"
                                  className="w-20 border border-gray-500 rounded-md px-2 py-1 text-sm bg-gray-600 text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                                />
                              ) : (
                                <>
                                  {(item.qtyInstalled || 0).toLocaleString()}{" "}
                                  <span className="text-gray-400 text-xs">
                                    {item.uom}
                                  </span>
                                </>
                              )}
                            </td>

                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatCurrency(perUnitSubtotal)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {editMode
                                ? formatCurrency(
                                    perUnitSubtotal *
                                      (quantities[item.lineItemId] || 0),
                                  )
                                : formatCurrency(displaySubtotal)}
                            </td>

                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {editMode
                                ? formatCurrency(
                                    perUnitBurden *
                                      (quantities[item.lineItemId] || 0),
                                  )
                                : formatCurrency(displayBurden)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right font-medium">
                              {editMode
                                ? formatCurrency(
                                    perUnitTotal *
                                      (quantities[item.lineItemId] || 0),
                                  )
                                : formatCurrency(displayTotal)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="13"
                          className="px-4 py-4 text-center text-sm text-gray-400"
                        >
                          No line items found
                          {bidAreaFilter && ` for bid area "${bidAreaFilter}"`}.
                        </td>
                      </tr>
                    )}
                    {filteredLineItems.length > 0 && (
                      <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500">
                        <td
                          className="px-4 py-3 text-sm text-orange-300 text-right"
                          colSpan="3"
                        >
                          TOTALS
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {filteredLineItems
                            .reduce(
                              (sum, item) => sum + (item.currentEstimated || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400 text-right font-bold">
                          {formatCurrency(
                            filteredLineItems.reduce(
                              (sum, item) =>
                                sum +
                                (item.budgetAmount != null
                                  ? item.budgetAmount
                                  : (item.currentEstimated || 0) *
                                    (item.budgetUnitCost ||
                                      item.unitCost ||
                                      0)),
                              0,
                            ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-400 text-right">
                          {filteredLineItems
                            .reduce(
                              (sum, item) => sum + (item.currentApproved || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-yellow-400 text-right">
                          {filteredLineItems
                            .reduce(
                              (sum, item) => sum + (item.currentPending || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {filteredLineItems
                            .reduce(
                              (sum, item) => sum + (item.currentAvailable || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {filteredLineItems
                            .reduce(
                              (sum, item) => sum + (item.qtyInstalled || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatCurrency(totals.subtotal)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatCurrency(totals.burden)}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400 text-right font-bold">
                          {formatCurrency(totals.total)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* ════════════════════════════════════════════════════════════
                 ADD ITEMS FROM WORK ORDER — shown only in edit mode
                 ════════════════════════════════════════════════════════════ */}
            {editMode && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddItems(!showAddItems)}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200 border border-gray-500"
                >
                  <PlusIcon className="size-4 mr-2" />
                  Add Items from Work Order
                  {addedItemCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-orange-400 text-gray-900 rounded-full font-bold">
                      {addedItemCount}
                    </span>
                  )}
                  {showAddItems ? (
                    <ChevronUpIcon className="size-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="size-4 ml-2" />
                  )}
                </button>

                {showAddItems && (
                  <div className="mt-4 bg-gray-600/50 rounded-lg border border-gray-500 p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-semibold text-orange-400">
                        Available Work Order Items
                      </h3>
                      {addItemBidAreas.length > 0 && (
                        <select
                          value={addItemBidAreaFilter}
                          onChange={(e) =>
                            setAddItemBidAreaFilter(e.target.value)
                          }
                          className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="">All Bid Areas</option>
                          {addItemBidAreas.map((area) => (
                            <option key={area} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {addItemsLoading ? (
                      <div className="text-center py-4 text-gray-400">
                        Loading available items...
                      </div>
                    ) : filteredAdditionalItems.length > 0 ? (
                      <>
                        <div className="overflow-x-auto rounded-lg">
                          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                            <thead className="bg-gray-800">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Title
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Bid Area
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Budget Qty
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Installed
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Pending
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Available
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Unit Cost
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Qty to Add
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                  Line Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-600">
                              {filteredAdditionalItems.map((item, index) => {
                                const { color, textColor, percentage } =
                                  getAvailabilityColor(
                                    item.qtyEstimated,
                                    item.qtyInstalled || item.qtyApproved || 0,
                                    item.qtyPending,
                                  );
                                const qty = parseFloat(
                                  addItemQuantities[item._id] || 0,
                                );
                                const lineTotal = qty * (item.unitCost || 0);
                                const hasQty = qty > 0;

                                return (
                                  <tr
                                    key={item._id}
                                    className={`${
                                      hasQty
                                        ? "bg-orange-400/10 border-l-2 border-l-orange-400"
                                        : index % 2 === 0
                                          ? "bg-gray-700"
                                          : "bg-gray-600"
                                    } hover:bg-gray-500 transition-colors duration-200`}
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-200">
                                      {item.title}
                                      {item.hasVariance && (
                                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-400/20 text-yellow-400 rounded">
                                          VAR
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-200">
                                      {item.bidArea || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                      {(
                                        item.qtyEstimated || 0
                                      ).toLocaleString()}{" "}
                                      <span className="text-gray-400 text-xs">
                                        {item.uom}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-green-400 text-right">
                                      {(
                                        item.qtyInstalled ||
                                        item.qtyApproved ||
                                        0
                                      ).toLocaleString()}{" "}
                                      <span className="text-gray-400 text-xs">
                                        {item.uom}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                                      {(item.qtyPending || 0).toLocaleString()}{" "}
                                      <span className="text-gray-400 text-xs">
                                        {item.uom}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                      <span className={textColor}>
                                        {(
                                          item.qtyRemaining || 0
                                        ).toLocaleString()}{" "}
                                        {item.uom}
                                      </span>
                                      <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                        <div
                                          className={`${color} h-1.5 rounded-full`}
                                          style={{
                                            width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                                          }}
                                        />
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                      ${(item.unitCost || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right">
                                      <input
                                        type="number"
                                        value={
                                          addItemQuantities[item._id] || ""
                                        }
                                        onChange={(e) =>
                                          handleAddItemQuantityChange(
                                            item._id,
                                            e.target.value,
                                          )
                                        }
                                        placeholder="0"
                                        min="0"
                                        max={item.qtyRemaining}
                                        className="w-20 border border-gray-500 rounded-md px-2 py-1 text-sm bg-gray-600 text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                                      />
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right font-medium">
                                      <span
                                        className={
                                          hasQty
                                            ? "text-orange-400"
                                            : "text-gray-500"
                                        }
                                      >
                                        {formatCurrency(lineTotal)}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {addedItemCount > 0 && (
                          <div className="mt-3 p-3 bg-gray-700 rounded-md flex justify-between items-center">
                            <span className="text-sm text-gray-300">
                              <span className="font-semibold text-orange-400">
                                {addedItemCount}
                              </span>{" "}
                              item(s) to be added
                            </span>
                            <span className="text-sm font-semibold text-orange-400">
                              Added Subtotal: {formatCurrency(addItemsSubtotal)}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400 py-4 text-center">
                        {additionalItems.length === 0
                          ? "All available items from this work order are already on the worksheet."
                          : `No items found for bid area "${addItemBidAreaFilter}".`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Totals Footer ── */}
            <div className="flex flex-wrap justify-end mt-4 gap-4">
              <p className="text-sm font-medium text-gray-400">
                Budget Total:{" "}
                <span className="text-lg text-orange-400 font-semibold">
                  {formatCurrency(budgetTotals.estimatedAmount)}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                Subtotal (Base Pay):{" "}
                <span className="text-lg text-gray-200 font-semibold">
                  {formatCurrency(totals.subtotal)}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                Burden:{" "}
                <span className="text-lg text-gray-200 font-semibold">
                  {formatCurrency(totals.burden)}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                Total Cost:{" "}
                <span className="text-lg text-orange-400 font-semibold">
                  {formatCurrency(totals.total)}
                </span>
              </p>
              {editMode && addItemsSubtotal > 0 && (
                <>
                  <span className="text-gray-600">|</span>
                  <p className="text-sm font-medium text-gray-400">
                    + New Items:{" "}
                    <span className="text-lg text-orange-300 font-semibold">
                      {formatCurrency(addItemsSubtotal)}
                    </span>
                  </p>
                </>
              )}
            </div>

            {/* ── Action Buttons ── */}
            {(log.status === "pending" || log.status === "rejected") && (
              <div className="flex justify-end space-x-4 mt-6">
                {editMode ? (
                  <>
                    <button
                      onClick={handleSaveChanges}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 disabled:opacity-50 transition-colors duration-200"
                    >
                      {actionLoading
                        ? "Saving..."
                        : addedItemCount > 0
                          ? `Save Changes (${addedItemCount} new)`
                          : "Save Changes"}
                    </button>
                    <button
                      onClick={exitEditMode}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {log.status === "pending" && (
                      <>
                        <button
                          onClick={() => showConfirmationDialog("approved")}
                          disabled={actionLoading}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-green-400 rounded-md hover:bg-green-500 disabled:opacity-50 transition-colors duration-200"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => showConfirmationDialog("rejected")}
                          disabled={actionLoading}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-red-400 rounded-md hover:bg-red-500 disabled:opacity-50 transition-colors duration-200"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {log.status === "rejected" && (
                      <button
                        onClick={() => showConfirmationDialog("pending")}
                        disabled={actionLoading}
                        className="px-4 py-2 text-sm font-semibold text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 disabled:opacity-50 transition-colors duration-200"
                      >
                        Reactivate
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="Confirm"
          cancelText="Cancel"
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default PieceworkerLogDetail;
